import { ClipboardEvent, KeyboardEvent, useEffect, useRef } from 'react';

import cn from '@/utils/cn';

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

const OtpInput = ({
  length = 6,
  value,
  onChange,
  onComplete,
  error,
  disabled,
  autoFocus = true,
}: OtpInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
    // Only on mount: subsequent renders shouldn't steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (index: number, digit: string): void => {
    const next = digits.slice();
    next[index] = digit;
    const joined = next.join('');
    onChange(joined);
    if (joined.length === length && !next.includes('')) {
      onComplete?.(joined);
    }
  };

  // Spreads `cleaned` across the boxes from `start`, focusing where it lands.
  // Shared by paste and by SMS autofill, which both deliver the whole code.
  const fillFrom = (start: number, cleaned: string): void => {
    const next = digits.slice();
    let cursor = cleaned.length >= length ? 0 : start;
    for (let i = 0; i < cleaned.length && cursor < length; i += 1) {
      next[cursor] = cleaned[i] ?? '';
      cursor += 1;
    }
    const joined = next.join('');
    onChange(joined);
    inputRefs.current[Math.min(cursor, length - 1)]?.focus();
    if (joined.length === length && !next.includes('')) {
      onComplete?.(joined);
    }
  };

  const handleChange = (index: number, raw: string): void => {
    const cleaned = raw.replace(/\D/g, '');
    // `autoComplete="one-time-code"` makes iOS offer "From Messages: 123456",
    // which arrives as a change event with the whole code in one box. Keeping
    // only the last character turned a tap-to-autofill into a one-digit OTP.
    if (cleaned.length > 1) {
      fillFrom(index, cleaned);
      return;
    }
    setDigit(index, cleaned);
    if (cleaned && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (index: number, e: ClipboardEvent<HTMLInputElement>): void => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) {
      return;
    }
    e.preventDefault();
    const next = digits.slice();
    let cursor = index;
    for (let i = 0; i < pasted.length && cursor < length; i += 1) {
      next[cursor] = pasted[i];
      cursor += 1;
    }
    const joined = next.join('');
    onChange(joined);
    const focusIndex = Math.min(cursor, length - 1);
    inputRefs.current[focusIndex]?.focus();
    if (joined.length === length && !next.includes('')) {
      onComplete?.(joined);
    }
  };

  return (
    <div>
      {/* A fixed-width flex row of `w-12` boxes measured a rigid 328px that
          could not shrink (a form control's flex min-width resolves to its
          specified size), overflowing a 262px card on every phone. A grid of
          equal fractional tracks fills whatever width is available instead,
          capped at the original 48px once there is room.

          `maxWidth` is what stops that from over-correcting. The tracks are
          `1fr`, so on a wide card (the two-column Request Access form) six of
          them share ~500px while each box stays capped at 48px - leaving every
          digit marooned in the middle of an 80px cell, which reads as a broken
          gap rather than one control. Capped at the natural size the boxes want
          (48px each + the 8px gaps between them), the row is snug when there is
          room and still shrinks to fit when there is not. Derived from `length`
          rather than hardcoded, so a 4- or 8-digit code stays correct. */}
      <div
        className="mx-auto grid w-full justify-items-center gap-1.5 sm:gap-2"
        style={{
          gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`,
          maxWidth: `${length * 3 + (length - 1) * 0.5}rem`,
        }}
      >
        {digits.map((digit, index) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            disabled={disabled}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={(e) => handlePaste(index, e)}
            aria-label={`Digit ${index + 1} of ${length}`}
            aria-invalid={!!error}
            className={cn(
              'h-12 w-full min-w-0 rounded-lg border bg-cream text-center text-xl font-semibold text-ink shadow-sm sm:max-w-12',
              'transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
              'disabled:cursor-not-allowed disabled:opacity-60',
              error ? 'border-danger focus:ring-danger focus:border-danger' : 'border-line',
            )}
          />
        ))}
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default OtpInput;
